import React, {useCallback, useContext} from 'react';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import app from '@src/helpers/app';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import ListOfChecklist from '@views/orders/ListOfChecklist';
import usePermissionGranted from '@src/hooks/usePermissionGranted';
import {IOrderProductModel} from '@src/@interfaces/models/Order/IOrderProductModel';
import NoRecordsFound from '@src/components/NoRecordsFound/NoRecordsFound';

export default function OrderProductsChecklists<Model extends IEmptyModel>(props: {
    orderId?: string | number | null,
    entityStatusId: React.ComponentProps<typeof ListOfChecklist>['currentStatusId'],
    checkedItems: React.ComponentProps<typeof ListOfChecklist>['checkedItems'],
    model: IOrderModel | IOrderProductModel,
    modelOptions?: Record<string, any>,
    rowRecord: Model,
    isDisabled?: boolean,
}) {
    const orderId = props.orderId;
    const orderProductId = props.rowRecord.id;
    const productId = props.rowRecord.product_id;

    const {modelOptions} = useContext(FormFieldItemPropsContext) ?? {};
    const [design] = useContext(FormFieldDesignContext);

    type Callback = Required<React.ComponentProps<typeof ListOfChecklist>>['onItemStateChanged'];
    const onChanged = useCallback<Callback>(async (item, state) => {
        await app('http').route('orders.products.change-checklist-item-state', {
            id: String(orderId),
            product: String(orderProductId),
        }, {
            headers: {'content-type': 'application/json'},
            body: {
                'controller-name': 'Orders',
                checklist_item_id: item.id,
                state,
            }
        });
    }, [orderId, orderProductId]);

    const canSee = usePermissionGranted('orders@can-see-product-checklist');
    const checklists = (props.modelOptions ?? modelOptions)?.checklists as any;
    if (!checklists || !canSee || !productId) {
        return <NoRecordsFound/>;
    }

    return <ListOfChecklist
            checklists={checklists}
            productId={productId}
            displayOnlyWithType="for-order-product"
            currentStatusId={props.entityStatusId}
            checkedItems={props.checkedItems}
            canChangeItemsStatePermission="orders@can-change-product-checklist-items"
            size={design.size}
            onItemStateChanged={onChanged}
            disabled={props.isDisabled || design.fieldsAreDisabled}
            readonly={design.fieldsAreReadOnly}
    />;
}
